import React, { useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material'
import { format } from 'date-fns'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  annotationPlugin
)

interface MeasurementsChartProps {
  data: any[]
  interval: string
  hiveId?: number
  title?: string
}

const getSensorLabel = (sensorType: string): string => {
  const labels: { [key: string]: string } = {
    't': 'Temperature (°C)',
    'h': 'Humidity (%)',
    'w': 'Weight (kg)',
    'l': 'Light (lux)',
    's': 'Sound (dB)',
    'p': 'Pressure (hPa)',
    't_i': 'Inside Temperature (°C)',
    't_o': 'Outside Temperature (°C)',
    'h_i': 'Inside Humidity (%)',
    'h_o': 'Outside Humidity (%)',
    'battery_voltage': 'Battery Voltage (V)',
    'signal_strength': 'Signal Strength (dBm)',
    'signal_noise': 'Signal Noise (dB)',
  }
  return labels[sensorType] || sensorType.replace('_', ' ').toUpperCase()
}

const getYAxisId = (sensorType: string): string => {
  if (sensorType.indexOf('t') === 0) return 'temperature'
  if (sensorType.indexOf('h') === 0) return 'humidity'
  if (sensorType.indexOf('w') === 0) return 'weight'
  if (sensorType === 'battery_voltage') return 'voltage'
  return 'default'
}

const MeasurementsChart: React.FC<MeasurementsChartProps> = ({
  data,
  interval,
  hiveId,
  title
}) => {
  const theme = useTheme()

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    const groupedData: { [key: string]: any[] } = {}
    
    data.forEach(measurement => {
      const sensorType = measurement.sensor_type || measurement.abbreviation || 'unknown'
      if (!groupedData[sensorType]) {
        groupedData[sensorType] = []
      }
      groupedData[sensorType].push({
        x: measurement.timestamp || measurement.date || measurement.created_at,
        y: measurement.value
      })
    })

    const sensorColors: { [key: string]: { border: string; background: string } } = {
      't': { border: '#ff6384', background: 'rgba(255, 99, 132, 0.1)' },
      'h': { border: '#36a2eb', background: 'rgba(54, 162, 235, 0.1)' },
      'w': { border: '#4bc0c0', background: 'rgba(75, 192, 192, 0.1)' },
      'l': { border: '#9966ff', background: 'rgba(153, 102, 255, 0.1)' },
      's': { border: '#ffcd56', background: 'rgba(255, 205, 86, 0.1)' },
      'p': { border: '#ff9f40', background: 'rgba(255, 159, 64, 0.1)' },
      'battery_voltage': { border: '#2c3e50', background: 'rgba(44, 62, 80, 0.1)' },
      'signal_strength': { border: '#27ae60', background: 'rgba(39, 174, 96, 0.1)' },
    }

    const datasets = Object.keys(groupedData).map((sensorType, index) => {
      const measurements = groupedData[sensorType]
      const colors = sensorColors[sensorType] || {
        border: `hsl(${index * 60}, 70%, 50%)`,
        background: `hsla(${index * 60}, 70%, 50%, 0.1)`
      }

      return {
        label: getSensorLabel(sensorType),
        data: measurements.sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()),
        borderColor: colors.border,
        backgroundColor: colors.background,
        fill: false,
        tension: 0.4,
        borderWidth: 2.5,
        borderJoinStyle: 'round' as const,
        pointRadius: 2,
        pointHitRadius: 15,
        pointHoverRadius: 6,
        spanGaps: true,
        yAxisID: getYAxisId(sensorType),
      }
    })

    return {
      datasets
    }
  }, [data])


  const options = useMemo(() => {
    // Dynamically determine which scales are needed
    const usedScales = new Set(chartData?.datasets.map(ds => ds.yAxisID) || []);
    const scales: any = {
      x: {
        type: 'time' as const,
        time: {
          unit: (interval === 'hour' ? 'hour' : interval === 'day' ? 'day' : 'week') as any,
          displayFormats: {
            hour: 'HH:mm',
            day: 'MMM dd',
            week: 'MMM dd'
          }
        },
        title: {
          display: false,
        }
      }
    };

    if (usedScales.has('temperature')) {
      scales.temperature = {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        grace: '10%',
        grid: { drawOnChartArea: true },
      };
    }
    
    if (usedScales.has('humidity')) {
      scales.humidity = {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        min: 0,
        max: 100,
      };
    }

    if (usedScales.has('weight')) {
      scales.weight = {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        grace: '10%',
      };
    }

    if (usedScales.has('voltage')) {
      scales.voltage = {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        grace: '10%',
      };
    }

    if (usedScales.has('default')) {
      scales.default = {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        grace: '10%',
      };
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: (chartData?.datasets?.length || 0) > 1,
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20,
            font: { size: 12, weight: 'bold' as const }
          }
        },
        tooltip: {
          mode: 'index' as const,
          position: 'nearest' as const,
          padding: 12,
          backgroundColor: 'rgba(255, 231, 191, 0.95)',
          titleColor: '#242424',
          bodyColor: '#242424',
          bodyFont: { weight: 'bold' as const },
          borderColor: '#F8B133',
          borderWidth: 1,
          displayColors: true,
          callbacks: {
            title: (context: any) => format(new Date(context[0].parsed.x), 'PPpp'),
            label: (context: any) => ` ${context.dataset.label}: ${context.parsed.y.toFixed(2)}`
          }
        }
      },
      scales
    };
  }, [interval, chartData])

  if (!chartData || chartData.datasets.length === 0) {
    return null
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
          {title || getSensorLabel(chartData.datasets[0].label)}
        </Typography>
        <Box sx={{ height: 250, position: 'relative', mt: 2 }}>
          <Line data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  )
}

export default MeasurementsChart
